import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './AILab.css';

// Mountain AI Lab (October 2026). Recruiters: this week's English lesson, built from their own texts
// (utils/aiLab.js), answered here and graded on the server. AJ and Monica: everyone's results.
const LAB_ADMIN_EMAILS = ['ajroy@mountainrecruiting.com', 'monica@mountainrecruiting.com'];
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const fmtWeek = d => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtWhen = d => d ? new Date(d).toLocaleString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) + ' ET' : '—';

// "**word**" in lesson text becomes a highlight
const Marked = ({ text }) => (
  <>{String(text || '').split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? <mark key={i}>{part.slice(2, -2)}</mark> : <React.Fragment key={i}>{part}</React.Fragment>)}</>
);

// Word-level diff for the "You wrote / Say it like this" pairs
function diffWords(a, b) {
  const A = a.split(/(\s+)/), B = b.split(/(\s+)/), n = A.length, m = B.length;
  const L = Array.from({ length: n + 1 }, () => new Int32Array(m + 1));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const was = [], now = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (A[i] === B[j]) { was.push(A[i]); now.push(B[j]); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) { was.push(A[i].trim() ? <s key={`a${i}`}>{A[i]}</s> : A[i]); i++; }
    else { now.push(B[j].trim() ? <ins key={`b${j}`}>{B[j]}</ins> : B[j]); j++; }
  }
  while (i < n) { was.push(A[i].trim() ? <s key={`a${i}`}>{A[i]}</s> : A[i]); i++; }
  while (j < m) { now.push(B[j].trim() ? <ins key={`b${j}`}>{B[j]}</ins> : B[j]); j++; }
  return [was, now];
}

function Question({ q, onAnswer, busy }) {
  const answered = q.choice !== undefined;
  return (
    <div className="al-q">
      {q.source === 'own_text' && <span className="al-q-src">You sent</span>}
      <div className="al-q-prompt">{q.source === 'own_text' ? `"${q.prompt}"` : q.prompt}</div>
      <div className="al-opts">
        {q.options.map((o, i) => {
          const cls = !answered ? '' : i === q.answer ? ' right' : i === q.choice ? ' wrong' : '';
          return (
            <button key={i} type="button" className={`al-opt${cls}`} disabled={answered || busy} onClick={() => onAnswer(q, i)}>{o}</button>
          );
        })}
      </div>
      {answered && <div className="al-why"><b className={q.correct ? 'ok' : 'no'}>{q.correct ? 'Correct.' : 'Not quite.'}</b> {q.why}</div>}
    </div>
  );
}

function GoalBar({ per100, team }) {
  if (per100 == null) return null;
  const goal = team != null && per100 > team ? Math.max(Math.round(team), Math.round(per100 * 0.65)) : Math.max(1, Math.floor(per100));
  const max = Math.max(per100, team || 0, goal) * 1.08 || 1;
  const pos = v => `${Math.min(100, (v / max) * 100)}%`;
  return (
    <div className="al-goal">
      <div className="al-goal-head"><span>Small mistakes per 100 words</span><span>Goal for this week: <b>under {goal}</b></span></div>
      <div className="al-track">
        <span className="al-fill" style={{ width: pos(per100) }} />
        {team != null && <span className="al-marker team" style={{ left: pos(team) }}><i>Team · {Number(team).toFixed(1)}</i></span>}
        <span className="al-marker goal" style={{ left: pos(goal) }}><i>Goal · {goal}</i></span>
        <span className="al-marker you" style={{ left: pos(per100) }}><i>You now · {per100}</i></span>
      </div>
    </div>
  );
}

function History({ history, currentWeek }) {
  const past = history.filter(h => h.weekStart !== currentWeek || h.status.startsWith('Done'));
  if (!past.length) return null;
  return (
    <section className="al-card">
      <h3>Your weekly scores</h3>
      <div className="al-table-wrap">
        <table className="al-table">
          <thead><tr><th>Week of</th><th>Status</th><th>Score</th><th>Mistakes per 100 words</th></tr></thead>
          <tbody>
            {past.map(h => (
              <tr key={h.weekStart}>
                <td>{fmtWeek(h.weekStart)}</td>
                <td><StatusPill status={h.status} /></td>
                <td>{h.score != null ? `${h.score} / ${h.total}` : '—'}</td>
                <td>{h.per100 != null ? h.per100 : '—'}{h.teamPer100 != null ? <span className="al-muted"> (team {Number(h.teamPer100).toFixed(1)})</span> : null}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

const PILL = { 'Done on Monday': 'ok', 'Done late': 'late', 'In progress': 'prog', 'Not started': 'no', 'Not enough texts': 'muted', 'Not assigned': 'muted' };
const StatusPill = ({ status }) => <span className={`al-pill ${PILL[status] || 'muted'}`}>{status}</span>;

function RecruiterView({ data, setData }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const m = data.module;

  const allQs = useMemo(() => (m ? [...m.habits.flatMap(h => h.questions), ...m.practice] : []), [m]);

  const answer = async (q, choice) => {
    setBusy(true); setError('');
    try {
      const { data: r } = await axios.post(`${API_URL}/api/ai-lab/modules/${m.id}/answer`, { questionId: q.id, choice }, { headers: headers() });
      const fill = x => (x.id === q.id ? { ...x, choice: r.choice, correct: r.correct, answer: r.answer, why: r.why } : x);
      setData(d => ({
        ...d,
        module: {
          ...d.module,
          habits: d.module.habits.map(h => ({ ...h, questions: h.questions.map(fill) })),
          practice: d.module.practice.map(fill),
          answered: r.answered,
          score: r.score ?? d.module.score,
          completedAt: r.completedAt || d.module.completedAt,
        },
      }));
      if (r.completedAt) {
        const { data: fresh } = await axios.get(`${API_URL}/api/ai-lab/me`, { headers: headers() });
        setData(fresh);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save your answer. Try again.');
    } finally {
      setBusy(false);
    }
  };

  if (!m) {
    return (
      <>
        <section className="al-card al-empty">
          <h2>{data.moduleStatus === 'Not enough texts' ? 'No lesson this week' : 'Your lesson is on its way'}</h2>
          <p>{data.moduleStatus === 'Not enough texts'
            ? 'You typed fewer than 10 texts to candidates last week, so there was not enough to build a lesson. See you next Monday.'
            : 'Each Monday morning your lesson is built from the texts you typed the week before. Check back shortly.'}</p>
        </section>
        <History history={data.history} currentWeek={data.weekStart} />
      </>
    );
  }

  const st = m.stats || {};
  const done = !!m.completedAt;
  const correctCount = allQs.filter(q => q.correct).length;
  const missed = allQs.filter(q => q.choice !== undefined && !q.correct);
  const sectionDone = qs => qs.every(q => q.choice !== undefined);
  const steps = [true, ...m.habits.map(h => sectionDone(h.questions)), sectionDone(m.practice)];

  return (
    <>
      <header className="al-hero">
        <span className="al-eyebrow">Mountain AI Lab · Week of {fmtWeek(m.weekStart)}</span>
        <h1>{m.headline}</h1>
        <div className="al-meta"><span>About 20 minutes</span><span>Due Monday</span><span>{m.total} questions</span></div>
      </header>

      <div className="al-progress" aria-label="Progress">
        <div className="al-steps" style={{ gridTemplateColumns: `repeat(${steps.length + 1}, 1fr)` }}>
          {steps.map((d, i) => <span key={i} className={d ? 'done' : ''} />)}
          <span className={done ? 'done' : ''} />
        </div>
        <div className="al-progress-row"><span>{done ? 'All done' : 'Answer every question to finish'}</span><span>{m.answered} of {m.total} answered</span></div>
      </div>

      {done && (
        <section className="al-card al-result">
          <div className="al-score"><b>{m.score} / {m.total}</b><span>{m.score >= Math.ceil(m.total * 0.8) ? 'Great work. This week is complete.' : 'Complete. The habits you missed will come back if they still show up in your texts.'}</span></div>
          <div className="al-muted">Finished {fmtWhen(m.completedAt)}</div>
          {missed.length > 0 && (
            <div className="al-missed">
              <h3>Questions you missed</h3>
              {missed.map(q => (
                <div key={q.id} className="al-missed-row">
                  <div className="al-missed-q">{q.source === 'own_text' ? `You sent: "${q.prompt}"` : q.prompt}</div>
                  <div className="al-missed-a"><span>Right answer:</span> {q.options[q.answer]}</div>
                  <div className="al-muted">{q.why}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="al-card">
        <span className="al-time">Your week · 2 min</span>
        <h2>{m.strengthTitle}</h2>
        <div className="al-win">
          <b>{Number(st.typed || 0).toLocaleString()}</b>
          <p>texts typed {st.periodLabel || 'in the last 7 days'}. {m.strength}</p>
        </div>
        <p>{m.gap}</p>
        <GoalBar per100={st.per100} team={st.teamPer100} />
        <div className="al-quick"><b>Your easiest win:</b> {m.easiestWin}</div>
        <ol className="al-habit-list">{m.habits.map((h, i) => <li key={i}>{h.title}</li>)}</ol>
      </section>

      {m.habits.map((h, i) => (
        <section className="al-card" key={i}>
          <span className="al-time">Habit {i + 1} of {m.habits.length} · 3 min</span>
          <h2>{h.title}</h2>
          <p className="al-rule"><Marked text={h.rule} /></p>
          <div className="al-pairs">
            {h.examples.map((e, k) => {
              const [was, now] = diffWords(e.was, e.now);
              return (
                <div className="al-pair" key={k}>
                  <div className="was"><span className="lbl">You wrote</span>{was}</div>
                  <div className="now"><span className="lbl">Say it like this</span>{now}</div>
                </div>
              );
            })}
          </div>
          <p className="al-tip"><Marked text={h.tip} /></p>
          {h.questions.map(q => <Question key={q.id} q={q} busy={busy} onAnswer={answer} />)}
        </section>
      ))}

      <section className="al-card">
        <span className="al-time">Practice · 5 min</span>
        <h2>Fix your own texts</h2>
        <p>Each one is a real text you sent. Pick the version a US recruiter would send.</p>
        {m.practice.map(q => <Question key={q.id} q={q} busy={busy} onAnswer={answer} />)}
        {!done && <div className="al-muted">{m.total - m.answered} questions left. Your score shows here when you finish.</div>}
        {done && <div className="al-score small"><b>{correctCount} / {m.total}</b><span>Your score this week</span></div>}
      </section>

      {error && <div className="al-error">{error}</div>}
      <History history={data.history} currentWeek={data.weekStart} />
    </>
  );
}

function AdminView() {
  const [data, setData] = useState(null);
  const [week, setWeek] = useState('');
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null); // { userId, name, history }
  const [notice, setNotice] = useState('');

  const load = async w => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/ai-lab/admin`, { headers: headers(), params: w ? { week: w } : {} });
      setData(d); setWeek(d.week); setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load results.');
    }
  };
  useEffect(() => { load(); }, []);

  const showHistory = async row => {
    if (open?.userId === row.userId) { setOpen(null); return; }
    try {
      const { data: d } = await axios.get(`${API_URL}/api/ai-lab/admin/users/${row.userId}`, { headers: headers() });
      setOpen({ userId: row.userId, name: row.name, history: d.history });
    } catch { setOpen(null); }
  };

  const generateMissing = async () => {
    try {
      await axios.post(`${API_URL}/api/ai-lab/admin/generate`, { week }, { headers: headers() });
      setNotice('Building missing lessons in the background. About 3 minutes per recruiter; refresh to see them.');
    } catch (err) {
      setNotice(err.response?.data?.error || 'Could not start.');
    }
  };

  if (error) return <div className="al-error">{error}</div>;
  if (!data) return <div className="al-muted al-loading">Loading…</div>;

  const counts = data.rows.reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {});
  const order = ['Done on Monday', 'Done late', 'In progress', 'Not started', 'Not enough texts', 'Not assigned'];

  return (
    <>
      <header className="al-hero">
        <span className="al-eyebrow">Mountain AI Lab · Results</span>
        <h1>Weekly English lessons</h1>
        <div className="al-meta"><span>Each recruiter gets a lesson every Monday, built from the texts they typed the week before. It's due the same Monday.</span></div>
      </header>

      <div className="al-admin-bar">
        <label htmlFor="al-week">Week of{' '}
          <select id="al-week" value={week} onChange={e => { setOpen(null); load(e.target.value); }}>
            {(data.weeks.includes(data.week) ? data.weeks : [data.week, ...data.weeks]).map(w => <option key={w} value={w}>{fmtWeek(w)}</option>)}
          </select>
        </label>
        <div className="al-counts">{order.filter(s => counts[s]).map(s => <span key={s}><StatusPill status={s} /> {counts[s]}</span>)}</div>
        {(counts['Not assigned'] || 0) > 0 && <button type="button" className="al-btn" onClick={generateMissing}>Build missing lessons</button>}
      </div>
      {notice && <div className="al-notice">{notice}</div>}

      <section className="al-card al-flush">
        <div className="al-table-wrap">
          <table className="al-table">
            <thead><tr><th>Recruiter</th><th>Status</th><th>Score</th><th>Answered</th><th>Finished</th><th>Mistakes per 100 words</th><th>Last week</th><th>Texts typed</th></tr></thead>
            <tbody>
              {data.rows.map(r => {
                const delta = r.per100 != null && r.prevPer100 != null ? Math.round((r.per100 - r.prevPer100) * 10) / 10 : null;
                return (
                  <React.Fragment key={r.userId}>
                    <tr className="al-row" onClick={() => showHistory(r)} title="Show past weeks">
                      <td><b>{r.name}</b></td>
                      <td><StatusPill status={r.status} /></td>
                      <td>{r.score != null ? `${r.score} / ${r.total}` : '—'}</td>
                      <td>{r.total ? `${r.answered} / ${r.total}` : '—'}</td>
                      <td>{fmtWhen(r.completedAt)}</td>
                      <td>{r.per100 != null ? r.per100 : '—'}{delta != null && delta !== 0 ? <span className={delta < 0 ? 'al-down' : 'al-up'}> {delta < 0 ? '▼' : '▲'} {Math.abs(delta)}</span> : null}</td>
                      <td>{r.prevPer100 != null ? r.prevPer100 : '—'}</td>
                      <td>{r.typed != null ? r.typed : '—'}</td>
                    </tr>
                    {open?.userId === r.userId && (
                      <tr className="al-hist-row"><td colSpan={8}>
                        <div className="al-hist">
                          <b>{open.name}: past weeks</b>
                          {open.history.length === 0 ? <span className="al-muted"> No lessons yet.</span> : (
                            <table className="al-table al-inner">
                              <thead><tr><th>Week of</th><th>Status</th><th>Score</th><th>Finished</th><th>Mistakes per 100 words</th></tr></thead>
                              <tbody>{open.history.map(h => (
                                <tr key={h.weekStart}><td>{fmtWeek(h.weekStart)}</td><td><StatusPill status={h.status} /></td><td>{h.score != null ? `${h.score} / ${h.total}` : '—'}</td><td>{fmtWhen(h.completedAt)}</td><td>{h.per100 ?? '—'}</td></tr>
                              ))}</tbody>
                            </table>
                          )}
                        </div>
                      </td></tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      <p className="al-muted al-foot">Mistakes per 100 words counts grammar, wording, missing words and spelling in a sample of 30 of the recruiter's typed texts (punctuation and capitals are coached but not counted). Lower is better. ▼ means fewer mistakes than last week.</p>
    </>
  );
}

export default function AILab({ user, onLogout }) {
  const isLabAdmin = LAB_ADMIN_EMAILS.includes(String(user?.email || '').toLowerCase());
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isLabAdmin) return;
    axios.get(`${API_URL}/api/ai-lab/me`, { headers: headers() })
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.error || 'Could not load your lesson.'));
  }, [isLabAdmin]);

  return (
    <div className="ai-lab">
      <SharedNav user={user} onLogout={onLogout} />
      <div className={`al-wrap${isLabAdmin ? ' al-wide' : ''}`}>
        {isLabAdmin ? <AdminView /> : error ? <div className="al-error">{error}</div>
          : !data ? <div className="al-muted al-loading">Loading…</div>
          : data.notRecruiter ? <section className="al-card al-empty"><h2>Mountain AI Lab</h2><p>Weekly English lessons are for recruiters. Results are visible to AJ and Monica.</p></section>
          : <RecruiterView data={data} setData={setData} />}
      </div>
    </div>
  );
}
